export const apiTestingModule = {
  id: "api-testing-enterprise",
  title: "Enterprise API Testing Strategies", 
  description: "Comprehensive API testing approaches including unit tests, integration tests, contract testing, and performance validation.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Design comprehensive API testing strategies for enterprise applications
- Implement automated testing pipelines with unit, integration, and contract tests
- Perform API performance testing and load validation
- Set up monitoring and observability for production API health

## Prerequisites
- Experience with REST APIs and HTTP protocols
- Basic understanding of testing concepts and frameworks
- Knowledge of CI/CD pipelines and automation

## Overview
API testing is critical for enterprise reliability, with untested APIs causing 64% of production incidents. This module covers testing strategies used by high-reliability organizations to ensure API quality and performance.

---

## Key Concepts

### Testing Pyramid for APIs

#### Unit Testing
**Function-Level Testing**: Test individual API functions and business logic
**Mocking Dependencies**: Isolate units from external dependencies
**Code Coverage**: Aim for 80%+ coverage of critical business logic

#### Integration Testing
**Service Integration**: Test API interactions with databases and external services
**End-to-End Flows**: Validate complete business workflows
**Environment Consistency**: Ensure tests work across dev, staging, and production

#### Contract Testing
**API Contract Validation**: Ensure API responses match documented schemas
**Consumer-Driven Contracts**: Test API changes don't break consumers
**Schema Evolution**: Validate backward compatibility during API updates

### API Testing Types

#### Functional Testing
**Request/Response Validation**: Verify correct data processing
**Business Logic Testing**: Ensure business rules are properly implemented
**Error Handling**: Test error scenarios and edge cases

#### Performance Testing
**Load Testing**: Validate API performance under expected traffic
**Stress Testing**: Determine breaking points and failure modes
**Spike Testing**: Test API behavior during traffic spikes

#### Security Testing
**Authentication Testing**: Verify access controls work correctly
**Authorization Testing**: Ensure proper permission enforcement
**Input Validation**: Test against injection attacks and malformed data

---

## Practical Examples

### Example 1: Comprehensive API Test Suite
Build a complete test suite for a user management API.

**Test Structure**:
- Unit tests for business logic validation
- Integration tests for database operations
- Contract tests for API schema compliance
- Performance tests for load validation

**Test Data Management**:
Use factories and fixtures to create consistent test data across all test types.

### Example 2: Contract Testing Implementation
Implement contract testing between API producer and consumers.

**Producer Contract Tests**:
Validate that API responses match published schemas and documentation.

**Consumer Contract Tests**: 
Ensure consumer applications can handle API responses correctly.

**Contract Versioning**:
Manage API evolution while maintaining backward compatibility.

### Example 3: Performance Testing Pipeline
Set up automated performance testing in CI/CD pipeline.

**Performance Benchmarks**:
- Response time: 95th percentile < 500ms
- Throughput: 1000 requests per second
- Error rate: < 0.1% under normal load

**Automated Alerts**:
Trigger alerts when performance degrades beyond acceptable thresholds.

---

## Hands-On Lab

### Lab: Complete API Testing Strategy
Implement comprehensive testing for an e-commerce API.

**API Endpoints to Test**:
- GET /api/products - Product catalog retrieval
- POST /api/orders - Order creation
- GET /api/orders/:id - Order details
- PUT /api/orders/:id/status - Order status updates

**Testing Requirements**:
1. Unit tests with 85%+ code coverage
2. Integration tests for database operations
3. Contract tests for schema validation
4. Performance tests for load scenarios
5. Security tests for authentication/authorization
6. Monitoring setup for production health

**Test Automation**:
Configure CI/CD pipeline to run all tests automatically on code changes.

**Test Reporting**:
Generate comprehensive test reports with coverage metrics, performance benchmarks, and security scan results.

---

## Assessment

### Testing Checklist
- [ ] Unit tests cover critical business logic
- [ ] Integration tests validate system interactions
- [ ] Contract tests ensure API compatibility
- [ ] Performance tests validate scalability requirements
- [ ] Security tests verify access controls
- [ ] Test data management strategy implemented
- [ ] CI/CD pipeline includes automated testing
- [ ] Test reports provide actionable insights
- [ ] Monitoring alerts on test failures

### Best Practices
**Test Isolation**: Each test should be independent and repeatable
**Data Cleanup**: Properly clean up test data after each test run
**Environment Parity**: Keep test environments consistent with production
**Continuous Testing**: Run tests automatically on every code change

This module ensures your APIs meet enterprise quality standards through comprehensive testing strategies.
`,
  duration: 45,
  codeExamples: [
    {
      title: "API Unit Testing with Jest",
      language: "javascript",
      code: `// Unit tests for user service business logic
const UserService = require('../services/UserService');
const UserRepository = require('../repositories/UserRepository');

// Mock external dependencies
jest.mock('../repositories/UserRepository');
jest.mock('../services/EmailService');

describe('UserService', () => {
  let userService;
  let mockUserRepository;

  beforeEach(() => {
    mockUserRepository = new UserRepository();
    userService = new UserService(mockUserRepository);
    jest.clearAllMocks();
  });

  describe('createUser', () => {
    it('should create user with valid data', async () => {
      // Arrange
      const userData = {
        username: 'testuser',
        email: 'test@example.com',
        password: 'SecurePassword123!'
      };
      
      const expectedUser = { 
        id: 1, 
        ...userData, 
        password: 'hashed_password',
        createdAt: new Date()
      };

      mockUserRepository.findByEmail.mockResolvedValue(null);
      mockUserRepository.create.mockResolvedValue(expectedUser);

      // Act
      const result = await userService.createUser(userData);

      // Assert
      expect(mockUserRepository.findByEmail).toHaveBeenCalledWith(userData.email);
      expect(mockUserRepository.create).toHaveBeenCalledWith({
        username: userData.username,
        email: userData.email,
        password: expect.any(String) // hashed password
      });
      expect(result).toEqual(expectedUser);
    });

    it('should throw error for duplicate email', async () => {
      // Arrange
      const userData = {
        username: 'testuser',
        email: 'existing@example.com',
        password: 'SecurePassword123!'
      };

      mockUserRepository.findByEmail.mockResolvedValue({ id: 1 });

      // Act & Assert
      await expect(userService.createUser(userData))
        .rejects.toThrow('Email already exists');
      
      expect(mockUserRepository.create).not.toHaveBeenCalled();
    });

    it('should validate password strength', async () => {
      // Arrange
      const userData = {
        username: 'testuser',
        email: 'test@example.com',
        password: 'weak'
      };

      // Act & Assert
      await expect(userService.createUser(userData))
        .rejects.toThrow('Password does not meet requirements');
    });
  });
});`
    },
    {
      title: "API Integration Testing", 
      language: "javascript",
      code: `// Integration tests for API endpoints
const request = require('supertest');
const app = require('../app');
const db = require('../database');

describe('User API Integration Tests', () => {
  let authToken;
  let testUser;

  beforeAll(async () => {
    // Setup test database
    await db.migrate.latest();
    await db.seed.run();
    
    // Create test user and get auth token
    testUser = await db('users').insert({
      username: 'testuser',
      email: 'test@example.com',
      password: 'hashedpassword'
    }).returning('*');
    
    authToken = generateJWT(testUser[0]);
  });

  afterAll(async () => {
    // Cleanup test database
    await db.migrate.rollback();
    await db.destroy();
  });

  beforeEach(async () => {
    // Reset data before each test
    await db('users').where('email', '!=', 'test@example.com').del();
  });

  describe('POST /api/users', () => {
    it('should create new user with valid data', async () => {
      const userData = {
        username: 'newuser',
        email: 'newuser@example.com',
        password: 'SecurePassword123!'
      };

      const response = await request(app)
        .post('/api/users')
        .set('Authorization', 'Bearer ' + authToken)
        .send(userData)
        .expect(201);

      expect(response.body).toHaveProperty('id');
      expect(response.body.username).toBe(userData.username);
      expect(response.body.email).toBe(userData.email);
      expect(response.body).not.toHaveProperty('password');

      // Verify user was created in database
      const dbUser = await db('users').where('email', userData.email).first();
      expect(dbUser).toBeTruthy();
    });

    it('should return 400 for invalid email format', async () => {
      const userData = {
        username: 'newuser',
        email: 'invalid-email',
        password: 'SecurePassword123!'
      };

      const response = await request(app)
        .post('/api/users')
        .set('Authorization', 'Bearer ' + authToken)
        .send(userData)
        .expect(400);

      expect(response.body.error).toContain('Invalid email format');
    });

    it('should return 401 without authentication', async () => {
      const userData = {
        username: 'newuser',
        email: 'newuser@example.com',
        password: 'SecurePassword123!'
      };

      await request(app)
        .post('/api/users')
        .send(userData)
        .expect(401);
    });
  });

  describe('GET /api/users/:id', () => {
    it('should return user details for valid ID', async () => {
      const response = await request(app)
        .get('/api/users/' + testUser[0].id)
        .set('Authorization', 'Bearer ' + authToken)
        .expect(200);

      expect(response.body.id).toBe(testUser[0].id);
      expect(response.body.username).toBe(testUser[0].username);
      expect(response.body).not.toHaveProperty('password');
    });

    it('should return 404 for non-existent user', async () => {
      await request(app)
        .get('/api/users/999999')
        .set('Authorization', 'Bearer ' + authToken)
        .expect(404);
    });
  });
});`
    },
    {
      title: "API Performance Testing",
      language: "javascript", 
      code: `// Performance testing with Artillery
const artillery = require('artillery');

// Artillery configuration for load testing
const loadTestConfig = {
  config: {
    target: 'http://localhost:3000',
    phases: [
      { duration: 60, arrivalRate: 10 }, // Warm up: 10 RPS for 1 minute
      { duration: 120, arrivalRate: 50 }, // Load test: 50 RPS for 2 minutes  
      { duration: 60, arrivalRate: 100 }, // Stress test: 100 RPS for 1 minute
    ],
    defaults: {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  },
  scenarios: [
    {
      name: 'Get product list',
      weight: 60,
      flow: [
        {
          get: {
            url: '/api/products',
            capture: {
              json: '$.data[0].id',
              as: 'productId'
            }
          }
        }
      ]
    },
    {
      name: 'Get product details',
      weight: 30,
      flow: [
        {
          get: {
            url: '/api/products/{{ productId }}'
          }
        }
      ]
    },
    {
      name: 'Create order',
      weight: 10,
      flow: [
        {
          post: {
            url: '/api/orders',
            headers: {
              'Authorization': 'Bearer {{ authToken }}'
            },
            json: {
              items: [
                { productId: '{{ productId }}', quantity: 1 }
              ],
              customerId: '{{ customerId }}'
            }
          }
        }
      ]
    }
  ]
};

// Custom performance testing with metrics collection
class PerformanceTester {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
    this.metrics = {
      requests: 0,
      failures: 0,
      responseTimes: [],
      errors: []
    };
  }

  async runLoadTest(concurrent = 10, duration = 60000) {
    console.log('Starting load test: ' + concurrent + ' concurrent users for ' + (duration/1000) + 's');
    
    const startTime = Date.now();
    const promises = [];

    for (let i = 0; i < concurrent; i++) {
      promises.push(this.simulateUser(startTime + duration));
    }

    await Promise.all(promises);
    return this.generateReport();
  }

  async simulateUser(endTime) {
    while (Date.now() < endTime) {
      try {
        const requestStart = Date.now();
        
        // Simulate realistic user behavior
        await this.makeRequest('GET', '/api/products');
        await this.sleep(Math.random() * 1000); // Random think time
        
        await this.makeRequest('GET', '/api/products/123');
        await this.sleep(Math.random() * 2000);
        
        const responseTime = Date.now() - requestStart;
        this.metrics.responseTimes.push(responseTime);
        this.metrics.requests++;
        
      } catch (error) {
        this.metrics.failures++;
        this.metrics.errors.push(error.message);
      }
    }
  }

  generateReport() {
    const avgResponseTime = this.metrics.responseTimes.reduce((a, b) => a + b, 0) / this.metrics.responseTimes.length;
    const p95ResponseTime = this.calculatePercentile(this.metrics.responseTimes, 95);
    const errorRate = (this.metrics.failures / this.metrics.requests) * 100;

    return {
      totalRequests: this.metrics.requests,
      totalFailures: this.metrics.failures,
      errorRate: errorRate.toFixed(2) + '%',
      avgResponseTime: avgResponseTime.toFixed(2) + 'ms',
      p95ResponseTime: p95ResponseTime.toFixed(2) + 'ms',
      throughput: (this.metrics.requests / 60).toFixed(2) + ' RPS'
    };
  }
}`
    }
  ],
  practice: {
    title: "Build Comprehensive API Testing Suite",
    description: "Create a complete testing strategy for a product catalog API including unit, integration, and performance tests.",
    starter: `// Basic API endpoint without tests
app.get('/api/products', async (req, res) => {
  const products = await database.getProducts();
  res.json(products);
});

app.post('/api/products', async (req, res) => {
  const product = await database.createProduct(req.body);
  res.json(product);
});`,
    solution: `// Comprehensive API testing implementation

// 1. Unit Tests
describe('ProductService', () => {
  beforeEach(() => {
    jest.clearAllMocks();
  });

  test('should validate product data before creation', async () => {
    const invalidProduct = { name: '', price: -10 };
    
    await expect(productService.createProduct(invalidProduct))
      .rejects.toThrow('Invalid product data');
  });

  test('should calculate discounted price correctly', () => {
    const product = { price: 100, discountPercent: 20 };
    const discountedPrice = productService.calculateDiscountedPrice(product);
    
    expect(discountedPrice).toBe(80);
  });
});

// 2. Integration Tests  
describe('Product API Integration', () => {
  beforeAll(async () => {
    await setupTestDatabase();
  });

  afterEach(async () => {
    await cleanupTestData();
  });

  test('should create product with valid data', async () => {
    const productData = {
      name: 'Test Product',
      description: 'Test Description',
      price: 99.99,
      categoryId: 1
    };

    const response = await request(app)
      .post('/api/products')
      .set('Authorization', 'Bearer ' + authToken)
      .send(productData)
      .expect(201);

    expect(response.body.name).toBe(productData.name);
    
    // Verify in database
    const dbProduct = await db('products').where('id', response.body.id).first();
    expect(dbProduct).toBeTruthy();
  });
});

// 3. Contract Tests
describe('Product API Contract', () => {
  test('GET /api/products response schema', async () => {
    const response = await request(app)
      .get('/api/products')
      .expect(200);

    const schema = {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              id: { type: 'number' },
              name: { type: 'string' },
              price: { type: 'number' },
              categoryId: { type: 'number' }
            },
            required: ['id', 'name', 'price']
          }
        },
        meta: {
          type: 'object',
          properties: {
            total: { type: 'number' },
            page: { type: 'number' }
          }
        }
      }
    };

    expect(response.body).toMatchSchema(schema);
  });
});

// 4. Performance Tests
describe('Product API Performance', () => {
  test('should handle 100 concurrent requests', async () => {
    const requests = Array(100).fill().map(() =>
      request(app).get('/api/products')
    );

    const responses = await Promise.all(requests);
    
    responses.forEach(response => {
      expect(response.status).toBe(200);
    });
  });

  test('should respond within 500ms under normal load', async () => {
    const startTime = Date.now();
    
    await request(app)
      .get('/api/products')
      .expect(200);
      
    const responseTime = Date.now() - startTime;
    expect(responseTime).toBeLessThan(500);
  });
});

// 5. Security Tests
describe('Product API Security', () => {
  test('should require authentication for POST requests', async () => {
    await request(app)
      .post('/api/products')
      .send({ name: 'Test Product' })
      .expect(401);
  });

  test('should prevent SQL injection', async () => {
    const maliciousInput = "'; DROP TABLE products; --";
    
    await request(app)
      .get('/api/products')
      .query({ search: maliciousInput })
      .expect(200); // Should not crash

    // Verify table still exists
    const products = await db('products').select().limit(1);
    expect(products).toBeDefined();
  });
});`
  },
  quiz: {
    question: "What percentage of code coverage is typically recommended for API unit tests in enterprise applications?",
    options: [
      "60-70%",
      "70-80%", 
      "80-90%",
      "90-100%"
    ],
    correctAnswer: 2,
    explanation: "80-90% code coverage is the recommended range for enterprise APIs, providing good protection while remaining practical to maintain and not requiring tests for every edge case."
  }
};